import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, ONEGO_LOGO } from "../lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [step, setStep] = useState("request"); // request | reset
    const [token, setToken] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const request = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/auth/forgot-password", { email });
            toast.success("Reset token issued (demo)");
            setToken(data.reset_token || "");
            setStep("reset");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: newPassword });
            toast.success("Password updated. You can now sign in.");
            setStep("done");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen og-gradient-soft flex items-center justify-center px-6 py-12" data-testid="forgot-page">
            <div className="w-full max-w-md og-card p-8">
                <Link to="/" className="inline-flex items-center">
                    <img src={ONEGO_LOGO} alt="OneGo" className="h-10 w-auto" />
                </Link>
                <h1 className="mt-6 font-display font-extrabold text-3xl text-onego-navy">
                    {step === "request" ? "Reset your password" : step === "reset" ? "Set a new password" : "All set"}
                </h1>

                {step === "request" && (
                    <form onSubmit={request} className="mt-6 space-y-4">
                        <p className="text-slate-600 text-sm">Enter your email to receive a reset token. (Demo: the token is returned in the response.)</p>
                        <input type="email" required className="og-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.africa" data-testid="forgot-email-input" />
                        <button disabled={loading} type="submit" className="og-btn-gradient w-full disabled:opacity-60" data-testid="forgot-submit-btn">
                            {loading ? "Sending\u2026" : "Send reset token"}
                        </button>
                    </form>
                )}

                {step === "reset" && (
                    <form onSubmit={submit} className="mt-6 space-y-4">
                        <p className="text-slate-600 text-sm">Paste your reset token and choose a new password.</p>
                        <input required className="og-input" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Reset token" data-testid="reset-token-input" />
                        <input required minLength={6} type="password" className="og-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password (6+ chars)" data-testid="reset-password-input" />
                        <button disabled={loading} type="submit" className="og-btn-gradient w-full disabled:opacity-60" data-testid="reset-submit-btn">
                            {loading ? "Saving\u2026" : "Update password"}
                        </button>
                    </form>
                )}

                {step === "done" && (
                    <div className="mt-6">
                        <p className="text-slate-600">Your password has been updated.</p>
                        <Link to="/login" className="og-btn-gradient inline-flex mt-4">Sign in</Link>
                    </div>
                )}

                <p className="mt-6 text-sm text-slate-600">
                    Remember it now?{" "}
                    <Link to="/login" className="font-bold text-onego-navy hover:text-pink-600">Sign in</Link>
                </p>
            </div>
        </div>
    );
}
